import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ChevronDown, Minus, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { deriveConversationTitle } from '@/lib/chat/title';
import {
  createConversation,
  fetchChatModels,
  fetchConversationMessages,
  fetchConversations,
  updateConversation,
} from '@/lib/chat/api';
import type { AgentUIMessage, ChatConversation, ChatModel, ChatSource, PendingAutoSend } from '@/lib/chat/types';
import { recordToUIMessage } from '@/lib/chat/utils';
import { ConversationSession } from './ConversationSession';
import { ConversationHistory } from './ConversationHistory';
import { analytics } from '@/lib/analytics';

const QUICK_ACTIONS = [
  'Summarize recent pages',
  'Compare two saved topics',
  'What did I save about caching?',
];
const CREATE_CONVERSATION_RETRY_DELAY_MS = 200;

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

interface ChatDockProps {
  totalPages: number;
  isMobile: boolean;
  initialOpen?: boolean;
  initialConversationId?: string | null;
  initialPrefill?: string;
  initialPrefillRequestKey?: string;
  onBeforeOpen?: () => void;
  onOpenInAppSource: (source: ChatSource) => void;
  onChatStateChange?: (state: { open: boolean; conversationId: string | null }) => void;
}

export function ChatDock({
  totalPages,
  isMobile,
  initialOpen = false,
  initialConversationId = null,
  initialPrefill = '',
  initialPrefillRequestKey = '',
  onBeforeOpen,
  onOpenInAppSource,
  onChatStateChange,
}: ChatDockProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [dockInput, setDockInput] = useState('');
  const [isDockFocused, setIsDockFocused] = useState(false);
  const [pendingAutoSend, setPendingAutoSend] = useState<PendingAutoSend | null>(null);

  const [models, setModels] = useState<ChatModel[]>([]);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(initialConversationId);
  const [selectedConversationMessages, setSelectedConversationMessages] = useState<AgentUIMessage[]>([]);

  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isFreshLaunchInFlight, setIsFreshLaunchInFlight] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // When we just created a conversation, skip the loadMessages call (we know it's empty)
  const skipNextLoadRef = useRef(false);
  const lastProcessedInitialPrefillRef = useRef<string | null>(null);
  const latestFreshLaunchSequenceRef = useRef(0);
  const latestMessageLoadSequenceRef = useRef(0);
  const isFreshLaunchInFlightRef = useRef(false);
  // Track whether bootstrap has run to avoid duplicate calls
  const hasBootstrappedRef = useRef(false);
  const bootstrapPromiseRef = useRef<Promise<void> | null>(null);
  const conversationsRef = useRef<ChatConversation[]>([]);
  const selectedConversationIdRef = useRef<string | null>(initialConversationId);

  const selectedConversation = useMemo(
    () => conversations.find(conversation => conversation.id === selectedConversationId) ?? null,
    [conversations, selectedConversationId],
  );

  const availableModels = useMemo(() => models.filter(model => model.available), [models]);

  const defaultModelId = useMemo(() => {
    const defaultModel = models.find(model => model.default) ?? models[0];
    return defaultModel?.id;
  }, [models]);

  const normalizedInitialPrefill = useMemo(() => initialPrefill.replace(/\s+/g, ' ').trim(), [initialPrefill]);
  const initialPrefillSignature = useMemo(() => {
    if (!normalizedInitialPrefill) return '';
    return initialPrefillRequestKey.trim() || normalizedInitialPrefill;
  }, [initialPrefillRequestKey, normalizedInitialPrefill]);
  const hasPendingPrefillLaunch = Boolean(
    initialPrefillSignature && lastProcessedInitialPrefillRef.current !== initialPrefillSignature,
  );
  const isInputLocked = isFreshLaunchInFlight || hasPendingPrefillLaunch;

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  const setFreshLaunchInFlight = useCallback((next: boolean) => {
    isFreshLaunchInFlightRef.current = next;
    setIsFreshLaunchInFlight(next);
  }, []);

  // --- Data loading ---

  const loadMessages = useCallback(async (conversationId: string) => {
    const loadSequence = latestMessageLoadSequenceRef.current + 1;
    latestMessageLoadSequenceRef.current = loadSequence;
    setIsMessagesLoading(true);
    try {
      const response = await fetchConversationMessages(conversationId, { limit: 100 });
      if (latestMessageLoadSequenceRef.current !== loadSequence) return;
      if (selectedConversationIdRef.current !== conversationId) return;
      const uiMessages = (response.messages ?? []).map(recordToUIMessage);
      setSelectedConversationMessages(uiMessages);
    } catch (error) {
      if (latestMessageLoadSequenceRef.current !== loadSequence) return;
      if (selectedConversationIdRef.current !== conversationId) return;
      const message = error instanceof Error ? error.message : 'Failed to load messages';
      setErrorMessage(message);
      setSelectedConversationMessages([]);
    } finally {
      if (latestMessageLoadSequenceRef.current === loadSequence) {
        setIsMessagesLoading(false);
      }
    }
  }, []);

  const loadConversations = useCallback(
    async (desiredConversationId?: string | null) => {
      setIsConversationsLoading(true);
      try {
        const result = await fetchConversations({ limit: 100 });
        const existingConversations = conversationsRef.current;
        let nextConversations = result;

        if (desiredConversationId && !nextConversations.some(conversation => conversation.id === desiredConversationId)) {
          const existingDesiredConversation = existingConversations.find(conversation => conversation.id === desiredConversationId);
          if (existingDesiredConversation) {
            nextConversations = [
              existingDesiredConversation,
              ...nextConversations.filter(conversation => conversation.id !== desiredConversationId),
            ];
          }
        }

        conversationsRef.current = nextConversations;
        setConversations(nextConversations);
        setSelectedConversationId(previousConversationId => {
          let nextConversationId = previousConversationId;

          if (desiredConversationId && nextConversations.some(conversation => conversation.id === desiredConversationId)) {
            nextConversationId = desiredConversationId;
          } else if (nextConversationId && nextConversations.some(conversation => conversation.id === nextConversationId)) {
            nextConversationId = previousConversationId;
          } else {
            nextConversationId = nextConversations[0]?.id ?? null;
          }

          selectedConversationIdRef.current = nextConversationId;
          return nextConversationId;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load conversations';
        setErrorMessage(message);
      } finally {
        setIsConversationsLoading(false);
      }
    },
    [],
  );

  const bootstrap = useCallback(async () => {
    if (hasBootstrappedRef.current) return;
    if (bootstrapPromiseRef.current) {
      await bootstrapPromiseRef.current;
      return;
    }

    setErrorMessage(null);
    const pendingBootstrap = (async () => {
      try {
        const [{ models: modelList }] = await Promise.all([fetchChatModels(), loadConversations(initialConversationId)]);
        setModels(modelList ?? []);
        hasBootstrappedRef.current = true;
      } catch (error) {
        hasBootstrappedRef.current = false; // Allow retry on error
        const message = error instanceof Error ? error.message : 'Failed to initialize chat';
        setErrorMessage(message);
      }
    })();

    bootstrapPromiseRef.current = pendingBootstrap;
    try {
      await pendingBootstrap;
    } finally {
      bootstrapPromiseRef.current = null;
    }
  }, [initialConversationId, loadConversations]);

  // Only bootstrap eagerly if the chat should open immediately (from URL or prefill)
  useEffect(() => {
    if (initialOpen || initialPrefill) {
      void bootstrap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedConversationId) {
      latestMessageLoadSequenceRef.current += 1;
      setIsMessagesLoading(false);
      setSelectedConversationMessages([]);
      return;
    }
    // Skip loading for freshly created conversations (we already set messages to [])
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      latestMessageLoadSequenceRef.current += 1;
      setIsMessagesLoading(false);
      return;
    }
    void loadMessages(selectedConversationId);
  }, [loadMessages, selectedConversationId]);

  // Use a ref for the callback to avoid re-firing when the callback reference changes
  // (e.g. when searchParams in Saved.tsx change, handleChatStateChange is recreated)
  const onChatStateChangeRef = useRef(onChatStateChange);
  onChatStateChangeRef.current = onChatStateChange;

  useEffect(() => {
    if (isFreshLaunchInFlightRef.current || hasPendingPrefillLaunch) {
      return;
    }
    onChatStateChangeRef.current?.({ open: isOpen, conversationId: selectedConversationId });
  }, [hasPendingPrefillLaunch, isOpen, selectedConversationId]);

  // chat_opened on transitions to open=true. Tracking the transition rather
  // than mount avoids re-firing when the dock state propagates through parents.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      analytics.track('chat_opened', {
        conversation_id: selectedConversationId,
      });
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
    }
  }, [isOpen, selectedConversationId]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // --- Conversation management ---

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (selectedConversationIdRef.current) return selectedConversationIdRef.current;

    const currentConversations = conversationsRef.current;
    if (currentConversations.length > 0) {
      const firstId = currentConversations[0]?.id ?? null;
      setSelectedConversationId(firstId);
      selectedConversationIdRef.current = firstId;
      return firstId;
    }

    try {
      const conversation = await createConversation({ modelId: defaultModelId });
      setConversations(prev => {
        const next = [conversation, ...prev];
        conversationsRef.current = next;
        return next;
      });
      setSelectedConversationId(conversation.id);
      selectedConversationIdRef.current = conversation.id;
      return conversation.id;
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create conversation');
      return null;
    }
  }, [defaultModelId]);

  const openChat = useCallback(async () => {
    onBeforeOpen?.();
    await bootstrap();
    await ensureConversation();
    setIsOpen(true);
  }, [bootstrap, ensureConversation, onBeforeOpen]);

  const handleCreateConversation = useCallback(async () => {
    try {
      const conversation = await createConversation({ modelId: defaultModelId });
      skipNextLoadRef.current = true;
      latestMessageLoadSequenceRef.current += 1;
      setIsMessagesLoading(false);
      setConversations(prev => {
        const next = [conversation, ...prev];
        conversationsRef.current = next;
        return next;
      });
      setSelectedConversationId(conversation.id);
      selectedConversationIdRef.current = conversation.id;
      setSelectedConversationMessages([]);
      setPendingAutoSend(null);
      setErrorMessage(null);
      return conversation.id;
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create conversation');
      return null;
    }
  }, [defaultModelId]);

  const startFreshConversationWithPrompt = useCallback(async (
    prompt: string,
    source: 'search-deeplink' | 'dock-input' | 'quick-action',
  ) => {
    const normalizedPrompt = prompt.replace(/\s+/g, ' ').trim();
    if (!normalizedPrompt) return;
    const launchSequence = latestFreshLaunchSequenceRef.current + 1;
    latestFreshLaunchSequenceRef.current = launchSequence;

    setPendingAutoSend(null);
    setFreshLaunchInFlight(true);

    onBeforeOpen?.();
    await bootstrap();
    if (latestFreshLaunchSequenceRef.current !== launchSequence) return;

    const title = deriveConversationTitle(normalizedPrompt) ?? undefined;
    const payload = { modelId: defaultModelId, title };
    let lastError: unknown = null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (latestFreshLaunchSequenceRef.current !== launchSequence) return;

      try {
        const conversation = await createConversation(payload);
        if (latestFreshLaunchSequenceRef.current !== launchSequence) return;

        skipNextLoadRef.current = true;
        latestMessageLoadSequenceRef.current += 1;
        setIsMessagesLoading(false);
        setFreshLaunchInFlight(false);
        setConversations(prev => {
          const next = [conversation, ...prev.filter(item => item.id !== conversation.id)];
          conversationsRef.current = next;
          return next;
        });
        setSelectedConversationId(conversation.id);
        selectedConversationIdRef.current = conversation.id;
        setSelectedConversationMessages([]);
        setPendingAutoSend({
          conversationId: conversation.id,
          text: normalizedPrompt,
          requestId: `${source}-${conversation.id}-${Date.now()}`,
        });
        setErrorMessage(null);
        setIsOpen(true);
        return;
      } catch (error) {
        if (latestFreshLaunchSequenceRef.current !== launchSequence) return;

        lastError = error;
        if (attempt === 0) {
          await wait(CREATE_CONVERSATION_RETRY_DELAY_MS);
        }
      }
    }

    if (latestFreshLaunchSequenceRef.current !== launchSequence) return;
    setFreshLaunchInFlight(false);
    setPendingAutoSend(null);
    setErrorMessage(lastError instanceof Error ? lastError.message : 'Failed to create conversation');
  }, [bootstrap, defaultModelId, onBeforeOpen, setFreshLaunchInFlight]);

  // Deep-link prefill should always start a fresh conversation and submit once.
  useEffect(() => {
    if (!normalizedInitialPrefill || !initialPrefillSignature) return;
    if (lastProcessedInitialPrefillRef.current === initialPrefillSignature) return;

    lastProcessedInitialPrefillRef.current = initialPrefillSignature;
    void startFreshConversationWithPrompt(normalizedInitialPrefill, 'search-deeplink');
  }, [initialPrefillSignature, normalizedInitialPrefill, startFreshConversationWithPrompt]);

  const handleRenameConversation = useCallback(async (conversation: ChatConversation) => {
    const currentTitle = conversation.title?.trim() || '';
    const nextTitle = window.prompt('Rename conversation', currentTitle);
    if (nextTitle === null) return;
    try {
      const updated = await updateConversation(conversation.id, { title: nextTitle.trim() });
      setConversations(prev => {
        const next = prev.map(item => (item.id === updated.id ? updated : item));
        conversationsRef.current = next;
        return next;
      });
      analytics.track('chat_thread_renamed', { conversation_id: updated.id });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to rename conversation');
    }
  }, []);

  const handleArchiveConversation = useCallback(async (conversation: ChatConversation) => {
    try {
      const updated = await updateConversation(conversation.id, { archived: true });
      setConversations(prev => {
        const next = prev.filter(item => item.id !== updated.id);
        conversationsRef.current = next;
        return next;
      });
      // Archive is the user-visible "delete" — no hard-delete UI exists.
      analytics.track('chat_thread_deleted', { conversation_id: updated.id, mode: 'archive' });
      if (selectedConversationId === updated.id) {
        const nextConversation = conversationsRef.current.find(item => item.id !== updated.id) ?? null;
        setSelectedConversationId(nextConversation?.id ?? null);
        selectedConversationIdRef.current = nextConversation?.id ?? null;
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to archive conversation');
    }
  }, [selectedConversationId]);

  const handleUpdateConversationModel = useCallback(async (conversationId: string, modelId: string) => {
    try {
      const updated = await updateConversation(conversationId, { modelId });
      setConversations(prev => {
        const next = prev.map(c => (c.id === updated.id ? updated : c));
        conversationsRef.current = next;
        return next;
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update model');
    }
  }, []);

  const handleConversationActivity = useCallback(async () => {
    await loadConversations(selectedConversationIdRef.current);
  }, [loadConversations]);

  // --- Dock input submit ---

  const handleDockSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = dockInput.trim();

    if (!trimmed) {
      await openChat();
      return;
    }

    // Create a fresh conversation for every new dock submission
    setDockInput('');
    await startFreshConversationWithPrompt(trimmed, 'dock-input');
  };

  const handleQuickAction = (action: string) => {
    setDockInput('');
    void startFreshConversationWithPrompt(action, 'quick-action');
  };

  // --- Render ---

  const conversationTitle = selectedConversation
    ? (selectedConversation.title?.trim() || 'Untitled conversation')
    : 'AI chat';
  const expandedMaxHeight = isMobile ? '85vh' : 'min(80vh, 52rem)';

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center pl-3 pr-20 sm:pl-4 md:pr-4">
      <div
        className={cn(
          'pointer-events-auto w-full rounded-sm border border-neutral-300 bg-neutral-100 p-1.5 shadow-sm',
          isOpen
            ? 'flex max-w-3xl flex-col overflow-hidden animate-in fade-in-0 slide-in-from-bottom-2 duration-200'
            : 'max-w-xl',
        )}
        style={isOpen ? { maxHeight: expandedMaxHeight } : undefined}
      >
        {isOpen ? (
          <>
            {/* Header */}
            <div className="flex h-12 shrink-0 items-center justify-between px-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <ConversationHistory
                  conversations={conversations}
                  selectedConversationId={selectedConversationId}
                  isLoading={isConversationsLoading}
                  onSelectConversation={setSelectedConversationId}
                  onRenameConversation={handleRenameConversation}
                  onArchiveConversation={handleArchiveConversation}
                  isMobile={isMobile}
                >
                  <button
                    type="button"
                    className="flex min-w-0 max-w-[min(45vw,24rem)] items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-neutral-800 hover:bg-neutral-200"
                  >
                    <span className="truncate">{conversationTitle}</span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                  </button>
                </ConversationHistory>
              </div>

              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleCreateConversation()}
                  className="h-8 gap-1.5 text-xs"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">New chat</span>
                </Button>

                {selectedConversation && (
                  <Select
                    value={selectedConversation.model_id}
                    onValueChange={value => {
                      void handleUpdateConversationModel(selectedConversation.id, value);
                    }}
                  >
                    <SelectTrigger className="h-8 w-auto gap-1.5 border-neutral-200 bg-neutral-50 px-2.5 text-xs text-neutral-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(availableModels.length > 0 ? availableModels : models).map(model => (
                        <SelectItem key={model.id} value={model.id} disabled={!model.available}>
                          {model.label}{model.available ? '' : ' (Unavailable)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 text-neutral-400 hover:text-neutral-600"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-hidden rounded-md">
              {!selectedConversation && (
                <div className="flex h-full items-center justify-center p-6 text-sm text-neutral-500">
                  Start or select a conversation.
                </div>
              )}

              {selectedConversation && isMessagesLoading && (
                <div className="flex h-full items-center justify-center p-6 text-sm text-neutral-500">
                  Loading messages...
                </div>
              )}

              {selectedConversation && !isMessagesLoading && (
                <ConversationSession
                  key={`${selectedConversation.id}:${selectedConversation.model_id}`}
                  conversationId={selectedConversation.id}
                  modelId={selectedConversation.model_id}
                  initialMessages={selectedConversationMessages}
                  totalPages={totalPages}
                  inputDisabled={isInputLocked}
                  pendingAutoSend={pendingAutoSend}
                  onPendingAutoSendConsumed={requestId => {
                    setPendingAutoSend(current => {
                      if (!current || current.requestId !== requestId) return current;
                      return null;
                    });
                  }}
                  onMessagesChange={setSelectedConversationMessages}
                  onConversationActivity={() => {
                    void handleConversationActivity();
                  }}
                  onOpenInAppSource={source => {
                    onOpenInAppSource(source);
                    if (isMobile) {
                      setIsOpen(false);
                    }
                  }}
                  onStatusChange={() => {}}
                />
              )}
            </div>

            {/* Error at bottom of expanded card */}
            {errorMessage && (
              <div className="mt-1.5 shrink-0 rounded-md border border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
                {errorMessage}
              </div>
            )}
          </>
        ) : (
          <>
            {errorMessage && (
              <p className="mb-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-center text-xs text-red-700">
                {errorMessage}
              </p>
            )}

            {/* Quick action pills on focus — left-aligned, no "Searching X pages" */}
            {isDockFocused && (
              <div className="mb-1.5 flex flex-wrap justify-start gap-2 animate-in fade-in-0 slide-in-from-bottom-1 duration-150">
                {QUICK_ACTIONS.map(action => (
                  <button
                    key={action}
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleQuickAction(action)}
                    className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] text-neutral-600 shadow-sm hover:border-neutral-300 hover:bg-neutral-50"
                  >
                    {action}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={handleDockSubmit} className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                <Sparkles className="h-5 w-5 text-muted-foreground" />
              </div>

              <Input
                value={dockInput}
                onChange={event => setDockInput(event.target.value)}
                onFocus={() => {
                  setIsDockFocused(true);
                  void bootstrap();
                }}
                onBlur={() => setIsDockFocused(false)}
                placeholder="Ask about your pages..."
                className="h-12 w-full rounded-sm border-0 bg-neutral-200 pl-11 pr-12 text-base font-light shadow-none text-neutral-800 placeholder:text-neutral-500 focus-visible:ring-0 focus-visible:ring-offset-0"
              />

              <Button
                type="submit"
                size="icon"
                className="absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
